import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  Stethoscope,
  Sparkles,
  LogOut,
  Phone,
  Mail as MailIcon,
  MapPin,
  Loader2,
  Image as ImageIcon,
  Search,
  CheckCircle2,
  AlertCircle,
  Clock,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { API_BASE, photoUrl, conceptUrl } from "../lib/api";
import AdminPayments from "../components/AdminPayments";
import AdminReports from "../components/AdminReports";
import AdminAnswerCards from "../components/AdminAnswerCards";
import OptInExport from "../components/OptInExport";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

function DownloadPhotosButton({ item }) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/admin/sessions/${item.session_id}/photos.zip`, { credentials: "include" });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const name = (item.customer?.name || "").replace(/[^A-Za-z0-9]+/g, "") || "Customer";
      const date = (item.created_at || new Date().toISOString()).slice(0, 10);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${name}-${date}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Could not download photos");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      data-testid={`admin-download-photos-${item.id}`}
      onClick={onClick}
      disabled={busy}
      size="sm"
      className="bg-[#092C15] hover:bg-emerald-800 text-white rounded-full"
    >
      {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Download size={14} className="mr-1.5" />}
      Download All Photos
    </Button>
  );
}

const txt = (v) => (Array.isArray(v) ? v.map((x) => (typeof x === "string" ? x : x?.title || x?.detail || JSON.stringify(x))).join(" · ") : typeof v === "string" ? v : "");

function ConceptsPanel({ item }) {
  const done = (item.concepts || []).filter((c) => c.status === "complete" || !c.status);
  if (!done.length) return null;
  return (
    <div data-testid={`admin-concepts-${item.id}`} className="mt-3 space-y-3">
      {done.map((c) => (
        <div key={c.id} className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-3">
          <div className="text-xs font-semibold text-[#092C15]">
            Version {c.version} · {c.parent_concept_id ? "Revision" : c.kind === "free" || !c.kind ? "Free concept" : c.kind === "whole_property" ? "Whole property view" : "Extra view"} · {c.paid_label}
            {c.completed_at || c.created_at ? ` · ${new Date(c.completed_at || c.created_at).toLocaleString()}` : ""}
          </div>
          {c.parent_concept_id && c.adjustment && <div className="text-xs text-slate-600 mt-0.5">Revision note: {c.adjustment}{c.comment ? ` — ${c.comment}` : ""}</div>}
          <div className="mt-2 grid grid-cols-2 gap-2">
            <figure>
              <img src={photoUrl(c.source_photo_id)} alt="Original" className="w-full rounded-lg border border-slate-200" />
              <figcaption className="text-[10px] font-bold text-slate-500 mt-0.5">ORIGINAL</figcaption>
            </figure>
            <a href={conceptUrl(c.id)} target="_blank" rel="noreferrer" data-testid={`admin-concept-image-${c.id}`}>
              <img src={conceptUrl(c.id)} alt={`Concept v${c.version}`} className="w-full rounded-lg border border-slate-200" />
              <div className="text-[10px] font-bold text-emerald-700 mt-0.5">GREENFLOW CONCEPT</div>
            </a>
          </div>
        </div>
      ))}
    </div>
  );
}

function SubmissionDetails({ item }) {
  const a = item.assessment;
  const intake = Object.entries(item.intake || {}).filter(([, v]) => v && (!Array.isArray(v) || v.length));
  if (!a && intake.length === 0) return null;
  const groups = a ? a.areas_of_concern || a.opportunities || [] : [];
  return (
    <details data-testid={`admin-details-${item.id}`} className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
      <summary className="cursor-pointer font-semibold text-[#092C15]">Preferences, intake answers &amp; full AI assessment</summary>
      {intake.length > 0 && (
        <dl className="mt-2 space-y-1">
          {intake.map(([k, v]) => (
            <div key={k}>
              <dt className="inline font-semibold capitalize">{k.replace(/_/g, " ")}: </dt>
              <dd className="inline text-slate-700">{Array.isArray(v) ? v.join(", ") : v}</dd>
            </div>
          ))}
        </dl>
      )}
      {a && (
        <div className="mt-3 space-y-2 text-slate-700">
          <p>{a.summary}</p>
          {txt(a.what_we_see) && <p><span className="font-semibold">What we see: </span>{txt(a.what_we_see)}</p>}
          {txt(a.observations) && <p><span className="font-semibold">Observations: </span>{txt(a.observations)}</p>}
          {groups.map((g) => (
            <div key={g.category}>
              <div className="font-semibold text-emerald-800">{g.category}</div>
              <ul className="list-disc pl-5">
                {(g.items || g.concepts || []).map((x, i) => <li key={i}>{x}</li>)}
              </ul>
            </div>
          ))}
          {a.recommended_direction && <p><span className="font-semibold">Recommended direction: </span>{a.recommended_direction}</p>}
          {Array.isArray(a.next_steps) && a.next_steps.length > 0 && (
            <div><span className="font-semibold">Next steps:</span>
              <ul className="list-disc pl-5">{a.next_steps.map((s, i) => <li key={i}>{typeof s === "string" ? s : `${s.title || ""}${s.detail ? ` — ${s.detail}` : ""}`}</li>)}</ul>
            </div>
          )}
          {txt(a.investigate) && <p><span className="font-semibold">To investigate: </span>{txt(a.investigate)}</p>}
          {txt(a.confidence_note) && <p className="text-xs text-slate-500">{txt(a.confidence_note)}</p>}
        </div>
      )}
      {item.manual_review_requested && (
        <div data-testid={`admin-manual-review-${item.id}`} className="mt-3 rounded-lg bg-amber-100 text-amber-900 px-3 py-2 font-semibold">Customer requested GreenFlow manual review</div>
      )}
      {item.concept && (
        <div className="mt-3 space-y-1 text-slate-700" data-testid={`admin-concept-${item.id}`}>
          <div className="font-semibold text-[#092C15]">GreenFlow Concept — status: {item.concept.status}{item.concept.version ? ` · v${item.concept.version}` : ""}</div>
          {item.concept_internal?.model && <div className="text-xs">Model: {item.concept_internal.provider}/{item.concept_internal.model}</div>}
          {item.concept_internal?.error && <div className="text-xs text-rose-700">Error: {item.concept_internal.error}</div>}
          <div className="text-xs">Disclaimer acknowledged: {item.disclaimer_ack?.accepted ? `Yes (${item.disclaimer_ack.accepted_at?.slice(0, 16).replace("T", " ")} UTC)` : "No"}</div>
          <div className="text-xs">Revision request: {item.revision_request ? `${item.revision_request.adjustment}${item.revision_request.comment ? ` — ${item.revision_request.comment}` : ""}` : "None"}</div>
        </div>
      )}
    </details>
  );
}

function StatusBadge({ status }) {
  const map = {
    sent: { icon: CheckCircle2, label: "Email sent", cls: "bg-emerald-100 text-emerald-700" },
    skipped_no_key: { icon: Clock, label: "Queued (no key)", cls: "bg-amber-100 text-amber-800" },
    error: { icon: AlertCircle, label: "Email error", cls: "bg-rose-100 text-rose-700" },
  };
  const def = map[status] || { icon: Clock, label: status || "pending", cls: "bg-slate-100 text-slate-700" };
  const Icon = def.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${def.cls}`}>
      <Icon size={12} /> {def.label}
    </span>
  );
}

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [overview, setOverview] = useState(null);
  const [query, setQuery] = useState("");
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!user?.is_admin) return;
    const load = async () => {
      try {
        const [a, b] = await Promise.all([
          fetch(`${API_BASE}/admin/consultations`, { credentials: "include" }).then((r) => r.json()),
          fetch(`${API_BASE}/admin/overview`, { credentials: "include" }).then((r) => r.json()),
        ]);
        setItems(a.items || []);
        setOverview(b);
      } catch {
        setItems([]);
      }
    };
    load();
  }, [user]);

  const filtered = useMemo(() => {
    if (!items) return null;
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const c = it.customer || {};
      return (
        (c.name || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q) ||
        (c.phone || "").toLowerCase().includes(q) ||
        (c.address || "").toLowerCase().includes(q) ||
        (it.pathway || "").toLowerCase().includes(q)
      );
    });
  }, [items, query]);

  if (loading) {
    return (
      <div className="min-h-[100dvh] gf-gradient-dark grid place-items-center text-emerald-200">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!user.is_admin)
    return (
      <Navigate
        to="/admin/login"
        replace
        state={{ deniedEmail: user.email }}
      />
    );

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-[100dvh] bg-[#f8faf8]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-30 bg-[#092C15] text-white border-b border-emerald-900/30">
        <div className="mx-auto max-w-6xl px-5 py-3 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2">
            <img src="/greenflow-logo-clean.webp" alt="GreenFlow Landscape Vision" className="h-12 w-auto rounded-md bg-white p-1" data-testid="admin-header-logo" />
          </Link>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-300">
              GreenFlow Vision
            </div>
            <div className="font-display font-semibold text-base">Admin Review</div>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-emerald-100/80 text-xs">
            {user.picture && (
              <img src={user.picture} alt="" className="h-7 w-7 rounded-full" />
            )}
            <div className="truncate max-w-[160px]">{user.email}</div>
          </div>
          <Button
            data-testid="admin-logout-button"
            onClick={handleLogout}
            variant="ghost"
            className="text-emerald-100 hover:text-white hover:bg-white/10"
          >
            <LogOut size={16} className="mr-1.5" /> Sign out
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-6 gf-fade-in">
        <section data-testid="admin-quick-start" className="mb-5 rounded-2xl bg-white border border-emerald-100 p-4 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px]">
            <div className="font-display font-bold text-[#092C15]">Use the app as Admin</div>
            <div className="text-xs text-slate-500">Opens in a new tab, free for you — no card. This Admin page stays open.</div>
          </div>
          <a data-testid="admin-new-diagnosis" href="/intake?pathway=diagnose" target="_blank" rel="noopener"
            className="inline-flex items-center h-11 px-5 rounded-full bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold transition-colors">
            New Diagnosis
          </a>
          <a data-testid="admin-new-vision" href="/intake?pathway=improve" target="_blank" rel="noopener"
            className="inline-flex items-center h-11 px-5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors">
            New Landscape Vision
          </a>
        </section>
        {/* Stats */}
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Submissions", value: overview?.consultations ?? "—" },
            { label: "Sessions", value: overview?.sessions ?? "—" },
            { label: "Photos", value: overview?.photos ?? "—" },
            {
              label: "Diagnose / Improve",
              value: overview
                ? `${overview.pathway.diagnose} / ${overview.pathway.improve}`
                : "—",
            },
          ].map((s) => (
            <div
              key={s.label}
              className="rounded-2xl bg-white border border-emerald-100 p-4"
            >
              <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-700">
                {s.label}
              </div>
              <div className="mt-1 font-display text-2xl font-bold text-[#092C15]">
                {s.value}
              </div>
            </div>
          ))}
        </section>
        <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
          <span data-testid="optin-count-badge" className="rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold px-3 py-1">
            {overview?.marketing_optins ?? "—"} opted in to updates
          </span>
          <OptInExport />
        </div>

        {/* Search */}
        <section className="mt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <Input
              data-testid="admin-search-input"
              className="pl-9 h-11 bg-white"
              placeholder="Search by name, email, phone, address, or pathway…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </section>

        {/* List */}
        <section className="mt-5 space-y-3">
          {filtered === null && (
            <div className="rounded-xl bg-white p-8 border border-slate-200 flex items-center justify-center text-slate-500 gap-2">
              <Loader2 className="animate-spin" size={16} /> Loading submissions…
            </div>
          )}
          {filtered && filtered.length === 0 && (
            <div
              data-testid="admin-empty-state"
              className="rounded-xl bg-white p-10 border border-slate-200 text-center text-slate-500"
            >
              <ImageIcon size={28} className="mx-auto text-slate-300" />
              <div className="mt-2 font-semibold">No submissions yet</div>
              <div className="text-sm">
                New consultation requests from customers will appear here.
              </div>
            </div>
          )}
          {filtered?.map((it) => {
            const c = it.customer || {};
            const isDiagnose = it.pathway === "diagnose";
            const PathIcon = isDiagnose ? Stethoscope : Sparkles;
            const toneBg = isDiagnose ? "bg-sky-50" : "bg-emerald-50";
            const toneText = isDiagnose ? "text-sky-700" : "text-emerald-700";
            return (
              <article
                key={it.id}
                data-testid={`admin-submission-${it.id}`}
                className="rounded-2xl bg-white border border-slate-200 p-4 sm:p-5"
              >
                <div className="flex items-start gap-3">
                  <div className={`h-11 w-11 rounded-xl ${toneBg} ${toneText} grid place-items-center shrink-0`}>
                    <PathIcon size={22} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="font-display text-lg font-bold text-[#092C15] truncate">
                        {c.name}
                      </div>
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-widest ${toneBg} ${toneText}`}
                      >
                        {isDiagnose ? "Diagnose" : "Improve"}
                      </span>
                      {it.consultation_requested && <StatusBadge status={it.email_status} />}
                      {!it.consultation_requested && (
                        <span data-testid={`admin-no-consult-${it.id}`} className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold bg-slate-100 text-slate-600">No consultation request yet</span>
                      )}
                      {it.customer_email_status === "sent" && (
                        <span data-testid={`admin-customer-confirm-${it.id}`} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-sky-100 text-sky-700">
                          <CheckCircle2 size={12} /> Customer confirmed
                        </span>
                      )}
                    </div>
                    <div className="mt-2 grid sm:grid-cols-2 gap-1 text-sm text-slate-700">
                      <a
                        className="inline-flex items-center gap-1.5 hover:text-emerald-700"
                        href={`mailto:${c.email}`}
                      >
                        <MailIcon size={14} /> {c.email}
                      </a>
                      <a
                        className="inline-flex items-center gap-1.5 hover:text-emerald-700"
                        href={`tel:${c.phone}`}
                      >
                        <Phone size={14} /> {c.phone}
                      </a>
                      <div data-testid={`admin-marketing-opt-in-${it.id}`} className="sm:col-span-2 text-xs">
                        <span className={`rounded-full px-2 py-0.5 font-semibold ${c.marketing_opt_in ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}`}>
                          Marketing updates: {c.marketing_opt_in ? "Opted in" : "Not opted in"}
                        </span>
                        {it.pathway === "improve" && (
                          <span data-testid={`admin-portfolio-consent-${it.id}`} className={`ml-2 rounded-full px-2 py-0.5 font-semibold ${it.portfolio_consent?.granted ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}`}>
                            Portfolio use: {it.portfolio_consent?.granted ? "Allowed" : "Not allowed"}
                          </span>
                        )}
                      </div>
                      <div className="inline-flex items-center gap-1.5 sm:col-span-2 text-slate-600">
                        <MapPin size={14} /> {[c.address, c.city, c.zip].filter(Boolean).join(", ")}
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-slate-400">
                      Submitted{" "}
                      {new Date(it.created_at).toLocaleString()} · {it.photo_count || 0} photo(s)
                    </div>
                  </div>
                </div>

                {it.notes && (
                  <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700">
                    <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-1">
                      Customer notes
                    </div>
                    {it.notes}
                  </div>
                )}

                <ConceptsPanel item={it} />
                <SubmissionDetails item={it} />

                {it.photos && it.photos.length > 0 && (
                  <div className="mt-3 grid grid-cols-4 sm:grid-cols-6 gap-2">
                    {it.photos.map((p) => (
                      <button
                        key={p.id}
                        data-testid={`admin-photo-thumb-${p.id}`}
                        onClick={() => setPreview(p)}
                        className="relative rounded-lg overflow-hidden border border-slate-200 bg-slate-100 aspect-square hover:ring-2 hover:ring-emerald-500 transition"
                      >
                        <img
                          src={photoUrl(p.id)}
                          alt="submission"
                          className="w-full h-full object-cover"
                        />
                        {p.is_primary && (
                          <span className="absolute bottom-1 left-1 bg-emerald-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                            PRIMARY
                          </span>
                        )}
                        {!p.is_primary && p.category && (
                          <span data-testid={`admin-photo-label-${p.id}`} className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[9px] leading-tight px-1 py-0.5 truncate">
                            {p.category}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                {it.photos && it.photos.length > 0 && (
                  <div className="mt-3 flex justify-end">
                    <DownloadPhotosButton item={it} />
                  </div>
                )}
              </article>
            );
          })}
        <AdminPayments />
        <AdminReports />
        <AdminAnswerCards />
        </section>
      </main>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl p-2 bg-black border-0">
          <DialogHeader className="sr-only">
            <DialogTitle>Photo preview</DialogTitle>
          </DialogHeader>
          {preview && (
            <img
              src={photoUrl(preview.id)}
              alt="full"
              className="w-full h-auto rounded-lg"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
